
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

interface AudioUploaderProps {
  onFileSelect: (file: File) => void;
  sessionType?: 'stem' | 'acapella';
}

const MAX_FILE_SIZE = 75 * 1024 * 1024; // 75MB in bytes

const AudioUploader = ({ onFileSelect, sessionType = 'stem' }: AudioUploaderProps) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      if (!file.type.startsWith('audio/')) {
        toast.error('Please upload an audio file');
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error('File size must be less than 75MB');
        return;
      }

      // PostHog tracking disabled
      // posthog.capture('Audio Upload Started', {
      //   fileName: file.name,
      //   fileSize: file.size,
      //   fileType: file.type,
      //   tool: sessionType === 'stem' ? 'AI Stem Splitter' : 'Acapella Extractor',
      //   sessionType // Also include raw sessionType for filtering
      // });
      onFileSelect(file);
    }
  }, [onFileSelect, sessionType]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.aac']
    },
    multiple: false,
    maxSize: MAX_FILE_SIZE
  });

  return (
    <div
      {...getRootProps()}
      className={`w-full p-8 rounded-lg cursor-pointer transition-colors bg-background
        ${isDragActive ? 'bg-audio-surface/50' : 'hover:bg-muted'}
        flex flex-col items-center justify-center gap-4`}
    >
      <input {...getInputProps()} />
      <Upload className="w-12 h-12 text-gray-400" />
      <div className="text-center">
        <p className="text-lg font-medium text-gray-300">Drag & drop your audio file here</p>
        <p className="text-sm text-gray-400">or click to select a file</p>
      </div>
      <Button 
        variant="outline" 
        className="mt-2 bg-white text-black hover:bg-gray-100 hover:text-black"
      >
        Upload audio
      </Button>
      <p className="text-xs text-gray-500">Supports MP3, WAV, M4A, AAC (max 15 minutes / 75MB)</p>
    </div>
  );
};

export default AudioUploader;
